import { ROLES } from '../core/constants/roles';
import { isAppVersionSupported } from '../core/config/appVersion';
import { APP_VERSION } from '../config/appVersion';
import { readRemoteConfigFlags } from './firebase/remoteConfigFlags';
import {
  usesFirebaseAuth,
  signOutUser,
} from './FirebaseAuthService';
import {
  isEmailMfaGatePendingFor,
  isEmailMfaVerifiedFor,
  markEmailMfaGatePending,
} from './emailMfaSession';
import type { AppUser } from '../types';

/**
 * Whether a signed-in user may enter the app (version + maintenance + email MFA).
 * Email MFA keeps the Auth session for verification, but blocks shell entry until
 * the Login challenge completes (avoids MFA flash → dashboard race).
 */
export async function canEnterApp(sessionUser: AppUser): Promise<boolean> {
  const remote = readRemoteConfigFlags();
  const { minAppVersion, maintenanceMode } = remote;

  // Gate on the running client build, not a field on the user profile.
  if (!isAppVersionSupported(APP_VERSION, minAppVersion)) return false;

  if (maintenanceMode && sessionUser.role !== ROLES.GOD) {
    if (usesFirebaseAuth()) await signOutUser();
    return false;
  }

  // God uses Auth App / SMS / support — never mailbox email MFA.
  if (usesFirebaseAuth() && sessionUser.role !== ROLES.GOD) {
    // Synchronous gate set right after password/OAuth success — blocks the race
    // where onAuthStateChanged enters the shell before Login shows email MFA.
    if (isEmailMfaGatePendingFor(sessionUser.id) && !isEmailMfaVerifiedFor(sessionUser.id)) {
      return false;
    }

    try {
      const { getEmailMfaStatus } = await import('./EmailMfaService.js');
      const status = await getEmailMfaStatus();
      if (status.enrolled && !isEmailMfaVerifiedFor(sessionUser.id)) {
        markEmailMfaGatePending(sessionUser.id);
        return false;
      }
    } catch {
      // Status check is best-effort; do not block login on transient failures.
    }
  }

  return true;
}
